import React, {forwardRef, useState, useImperativeHandle, useEffect} from 'react';
import clsx from 'clsx';
import {makeStyles} from 'tss-react/mui';
import Grid from '@mui/material/Grid';
import {
   PtrnMaestroDetalleTabsProps,
   PtrnMaestroDetalleTabsRef
} from './PtrnMaestroDetalleTabsTypes';
import {PtrnAccionesProps, PtrnMainContentProps} from '../patronProps';
import PtrnContainerSimple from '../components/PtrnContainerSimple';
import PtrnTitulo from '../components/PtrnTitulo';
import {PtrnEstilosGenerales, PtrnPropsUtil} from '../patronUtils';
import PtrnTabs from '../components/CampoEditable/PtrnTabs';
import PtrnAccionButton from '../components/PtrnAccionButton';
import {PtrnState, PtrnTipoAccion} from '../patronTypes';

const useStyles = makeStyles<number>()((theme, maxHeightVhMain) => ({
   containerMainContent: PtrnEstilosGenerales.containerMainContent(maxHeightVhMain),
   borderContent: {
      border: '1px solid lightgray',
      borderRadius: 5
   }
}));

const PtrnMaestroDetalleTabs = <T,>(
   {
      ptrnRef,
      state,
      titulo,
      tituloXs,
      tabsXs,
      maxHeightVhMain,
      ocultarHeader,
      esActualizar,
      tabList,
      genTabProps,
      onCloseTab,
      onSelectedTabChange,
      accionInsert,
      accionUpdate,
      accionDelete,
      accionCancel,
      deshabilitarInsert,
      deshabilitarUpdate,
      deshabilitarDelete,
      localizacion,
      footerGridProps,
      children,
      noMarginContainer,
      rootGridProps,
      bodyGridProps
   }: PtrnMaestroDetalleTabsProps<T> & PtrnMainContentProps & PtrnAccionesProps,
   ref: React.Ref<PtrnMaestroDetalleTabsRef>
) => {
   const {classes} = useStyles(maxHeightVhMain);

   //#region ctrl tabs
   const [selectedTab, setSelectedTab] = useState(0);

   useEffect(() => {
      //asegura q siempre este visible un tab valido
      if (tabList.length > 0 && tabList.length < selectedTab + 1) {
         setSelectedTab(tabList.length - 1);
      }
   }, [tabList, selectedTab]);

   useEffect(() => {
      if (onSelectedTabChange) {
         onSelectedTabChange(selectedTab, tabList[selectedTab]);
      }
   }, [selectedTab]);

   function handleSelectedTabChange(_, newValue) {
      setSelectedTab(newValue);
   }
   //#endregion

   const {disabledInsert, disabledUpdate, disabledDelete} = PtrnPropsUtil.getDisabledAcciones(
      {deshabilitarInsert, deshabilitarUpdate, deshabilitarDelete},
      state as PtrnState
   );

   //#region vars retornos

   useImperativeHandle(ref, () => {
      return {
         selectedTab,
         setSelectedTab: (inx) => handleSelectedTabChange(null, inx)
      };
   });
   const idCompPrefix = 'ptrn-md-tabs';
   //#endregion
   return (
      <PtrnContainerSimple id={`${idCompPrefix}-container`} noMargin={noMarginContainer}>
         <Grid id={`${idCompPrefix}-grid-root`} container spacing={1} {...rootGridProps}>
            {!ocultarHeader && (
               <Grid
                  id={`${idCompPrefix}-grid-header`}
                  container
                  alignItems="center"
                  spacing={1}
                  item
                  xs={12}
                  paddingBottom={1}
               >
                  <Grid id={`${idCompPrefix}-grid-header-titulo`} item xs={tituloXs || 6}>
                     <PtrnTitulo>{titulo}</PtrnTitulo>
                  </Grid>
                  <Grid
                     id={`${idCompPrefix}-grid-header-tabs`}
                     container
                     item
                     xs={tabsXs || 6}
                     justifyContent="flex-end"
                  >
                     <PtrnTabs
                        tabList={tabList}
                        genTabProps={genTabProps}
                        selectedTab={selectedTab}
                        onSelectedTabChange={handleSelectedTabChange}
                        onCloseTab={onCloseTab}
                     />
                  </Grid>
               </Grid>
            )}
            <Grid
               container
               alignItems="flex-start"
               spacing={1}
               {...bodyGridProps}
               className={clsx(classes.containerMainContent, {
                  [classes.borderContent]: !ocultarHeader
               })}
               id={`${idCompPrefix}-grid-body-content`}
               item
               xs={12}
            >
               {children}
            </Grid>
            <Grid
               alignItems="center"
               justifyContent={esActualizar ? 'space-between' : 'flex-end'}
               spacing={1}
               {...footerGridProps}
               style={{...footerGridProps?.style, paddingLeft: 0}}
               id={`${idCompPrefix}-grid-footer`}
               container
               item
               xs={12}
            >
               {accionDelete && esActualizar && (
                  <Grid item xs={3} style={{paddingLeft: 0}}>
                     <PtrnAccionButton
                        name={`${idCompPrefix}-btn-delte`}
                        accion={{...accionDelete, tipo: PtrnTipoAccion.negativa}}
                        camposData={state}
                        ptrnRef={ptrnRef || null}
                        fullWidth
                        disabled={disabledDelete}
                     />
                  </Grid>
               )}
               <Grid container item xs={9} spacing={1} justifyContent="flex-end">
                  {accionCancel && (
                     <Grid item xs={4}>
                        <PtrnAccionButton
                           name={`${idCompPrefix}-btn-cancel`}
                           accion={{...accionCancel, tipo: PtrnTipoAccion.neutra}}
                           camposData={state}
                           ptrnRef={ptrnRef || null}
                           fullWidth
                        />
                     </Grid>
                  )}
                  {accionInsert && !esActualizar && (
                     <Grid item xs={4}>
                        <PtrnAccionButton
                           name={`${idCompPrefix}-btn-insert`}
                           accion={{...accionInsert, tipo: PtrnTipoAccion.positiva}}
                           camposData={state}
                           ptrnRef={ptrnRef || null}
                           fullWidth
                           disabled={disabledInsert}
                        />
                     </Grid>
                  )}
                  {accionUpdate && esActualizar && (
                     <Grid item xs={4}>
                        <PtrnAccionButton
                           name={`${idCompPrefix}-btn-update`}
                           accion={{...accionUpdate, tipo: PtrnTipoAccion.positiva}}
                           camposData={state}
                           ptrnRef={ptrnRef || null}
                           fullWidth
                           disabled={disabledUpdate}
                        />
                     </Grid>
                  )}
               </Grid>
            </Grid>
         </Grid>
      </PtrnContainerSimple>
   );
};
export default forwardRef(PtrnMaestroDetalleTabs) as <T>(
   props: PtrnMaestroDetalleTabsProps<T> &
      PtrnMainContentProps &
      PtrnAccionesProps & {ref?: React.Ref<PtrnMaestroDetalleTabsRef>}
) => React.ReactElement;
